import React from "react"
import PropTypes from "prop-types"
import styles from "../ArticleCard/index.module.scss"
import { omitText, Typography } from "@kohler-dsm-library/kohler-core"
import { renderRichText, applyLocaleToUrl } from "@/utils/formatters"
import { useRouter } from "next/router"

const ArticleCard = ({ articleData = {} }) => {
  const { locale } = useRouter()

  if (!articleData) return null

  const {
    background,
    heading,
    subHeading,
    image,
    ctas = [],
    showVideo = false,
    videoUrl = "",
    borderLine = true,
    longVideoUrl = "",
  } = articleData

  const imageUrl = image?.fields?.imageUrl
  const { asset = {} } = image?.fields || {}
  const { file = {} } = asset?.fields || {}
  const { url: assetUrl = "" } = file
  const ctasArray = Array.isArray(ctas) ? ctas : []
  const description = articleData?.description
    ? { text: renderRichText(articleData?.description, locale) }
    : null

  const validBackgrounds = ["white", "black"]
  const bg = validBackgrounds.includes(background) ? background : "white"
  const isWhiteBg = bg === "white"
  const hasImage = Boolean(imageUrl || assetUrl)
  const hasHeading = Boolean(heading)
  const hasSubHeading = Boolean(subHeading)
  const hasDescription = Boolean(description)

  const onlyImageMode =
    hasImage && !hasHeading && !hasSubHeading && !hasDescription
  return (
    <section
      className={`${styles.articleSection} ${
        isWhiteBg ? styles.whiteBackground : ""
      }`}
    >
      <div
        className={`${styles.articleContainer} ${
          !hasImage ? styles.noImageContainer : ""
        } ${!hasImage && !hasHeading && !hasSubHeading ? styles.onlyRichDescription : ""}`}
      >
        {onlyImageMode && (hasImage || showVideo) && (
          <div
            className={
              showVideo ? `${styles.onlyVideo}` : `${styles.onlyImageContainer}`
            }
          >
            <div
              className={
                showVideo
                  ? `${styles.onlyVideoWrapper}`
                  : `${styles.fullImageWrapper}`
              }
            >
              {onlyImageMode && !showVideo && (
                <img
                  src={imageUrl || assetUrl}
                  alt={image?.fields?.altText || "Feature Image"}
                  className={styles.fullImage}
                />
              )}
              {showVideo && (
                <div className={styles["video-wrapper"]}>
                  <iframe
                    className={styles["iframe-fullVideo"]}
                    title="Article video content"
                    src={videoUrl || longVideoUrl}
                    allowFullScreen
                  />
                </div>
              )}
            </div>
            {ctasArray.length > 0 && (
              <div className={styles.ctaContainer}>
                {ctasArray.map((cta, index) => {
                  const {
                    label,
                    url,
                    mediaUrl,
                    ctaType,
                    openerType = "",
                  } = cta?.fields || {}

                  if (!label) return null

                  if (ctaType === "Button") {
                    return (
                      <li key={index}>
                        <a
                          key={index}
                          href={applyLocaleToUrl(url || mediaUrl, locale)}
                          target={openerType === "New Tab" ? "_blank" : "_self"}
                          className={`${styles.articlePagesTextLink} ${
                            isWhiteBg
                              ? styles.whiteBgButton
                              : styles.blackBgButton
                          }`}
                        >
                          {label}
                        </a>
                      </li>
                    )
                  }
                  if (ctaType === "Text Link") {
                    return (
                      <li key={index}>
                        <a
                          key={index}
                          href={applyLocaleToUrl(url || mediaUrl, locale)}
                          target={openerType === "New Tab" ? "_blank" : "_self"}
                          className={`${styles.articleTextLink} ${
                            isWhiteBg
                              ? styles.textLinkBlackBg
                              : styles.textLinkWhiteBg
                          }`}
                        >
                          {label}
                        </a>
                      </li>
                    )
                  }
                  return null
                })}
              </div>
            )}
          </div>
        )}

        {!onlyImageMode && (
          <>
            {(hasImage || showVideo) && (
              <div className={styles.articleImageWrapper}>
                {hasImage && !showVideo && (
                  <img
                    src={imageUrl || assetUrl}
                    alt={image?.fields?.altText || ""}
                    className={styles.articleImage}
                  />
                )}
                {showVideo && (
                  <iframe
                    className={styles["iframe-video"]}
                    title="Article video content"
                    src={videoUrl || longVideoUrl}
                    allowFullScreen
                  />
                )}
              </div>
            )}
            <div className={styles.articleContent}>
              {heading && <h2 className={styles.articleHeading}>{heading}</h2>}
              {subHeading && (
                <h3 className={styles.articleSubHeading}>{subHeading}</h3>
              )}
              {description && (
                <Typography
                  component="div"
                  textColor={isWhiteBg ? "#232323" : "#fff"}
                  {...omitText(description)}
                  className={styles.typographyContent}
                >
                  {description.text}
                </Typography>
              )}
              {ctasArray.length > 0 && (
                <div className={styles.ctaContainer}>
                  {ctasArray.map((cta, index) => {
                    const {
                      label,
                      url,
                      mediaUrl,
                      ctaType,
                      openerType = "",
                    } = cta?.fields || {}

                    if (!label) return null

                    if (ctaType === "Button") {
                      return (
                        <li key={index}>
                          <a
                            key={index}
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            className={`${styles.articlePagesTextLink} ${
                              isWhiteBg
                                ? styles.whiteBgButton
                                : styles.blackBgButton
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      )
                    }
                    if (ctaType === "Text Link") {
                      return (
                        <li key={index}>
                          <a
                            target={
                              openerType === "New Tab" ? "_blank" : "_self"
                            }
                            key={index}
                            href={applyLocaleToUrl(url || mediaUrl, locale)}
                            className={`${styles.articleTextLink} ${
                              isWhiteBg
                                ? styles.textLinkBlackBg
                                : styles.textLinkWhiteBg
                            }`}
                          >
                            {label}
                          </a>
                        </li>
                      )
                    }

                    return null
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className={styles.dividerContainer}>
        {borderLine && <div className={styles.cardDivider}></div>}
      </div>
    </section>
  )
}

ArticleCard.propTypes = {
  articleData: PropTypes.shape({
    background: PropTypes.string,
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    image: PropTypes.object,
    ctas: PropTypes.array,
    showVideo: PropTypes.bool,
    videoUrl: PropTypes.string,
    description: PropTypes.object,
  }),
}

export default ArticleCard
